import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { EmojiPicker } from 'components/EmojiPicker/EmojiPicker';
import { useSendMessageMutation } from 'store/services/message/messageApi';
import { useSummonAdminMutation, useGetTradeCaseQuery } from 'store/services/case/caseApi';
import { AssetUrls } from 'config/assets';
import './ContactUserModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  receiverId: number;
  receiverUsername: string;
  receiverAvatar?: string;
  tradeId?: string;
}

export const ContactUserModal: React.FC<Props> = ({
  isOpen,
  onClose,
  receiverId,
  receiverUsername,
  receiverAvatar,
  tradeId,
}) => {
  const [message, setMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [rateLimitRetryAfter, setRateLimitRetryAfter] = useState(0);
  const [hasJustSummoned, setHasJustSummoned] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);

  const [sendMessage, { isLoading }] = useSendMessageMutation();
  const [summonAdmin, { isLoading: isSummoning }] = useSummonAdminMutation();

  // Check if case exists for this trade (only if tradeId provided)
  const { data: caseData, refetch: refetchCase } = useGetTradeCaseQuery(tradeId!, {
    skip: !isOpen || !tradeId
  });

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setMessage('');
      setShowEmojiPicker(false);
      setRateLimitRetryAfter(0);
      setHasJustSummoned(false);
    }
  }, [isOpen]);

  // Rate limit countdown
  useEffect(() => {
    if (rateLimitRetryAfter > 0) {
      const timer = setInterval(() => {
        setRateLimitRetryAfter(prev => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [rateLimitRetryAfter]);

  const handleEmojiSelect = (emoji: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newMessage = message.substring(0, start) + emoji + message.substring(end);

    setMessage(newMessage);

    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  const handleSend = async () => {
    if (!message.trim() || message.length > 2000) return;

    try {
      await sendMessage({
        receiverId,
        content: message.trim(),
      }).unwrap();

      toast.success(`Message sent to ${receiverUsername}!`);
      onClose();
    } catch (error: any) {
      console.error('Failed to send message:', error);

      if (error.status === 429) {
        const retrySeconds = parseInt(error.headers?.get('retry-after')) || 60;
        setRateLimitRetryAfter(retrySeconds);
        toast.error(
          `Too many messages. Please wait ${retrySeconds} seconds before trying again.`,
          { autoClose: retrySeconds * 1000 }
        );
      } else {
        toast.error(error.data?.message || 'Failed to send message. Please try again.');
      }
    }
  };

  const handleSummonAdmin = async () => {
    if (!tradeId) return;

    try {
      setHasJustSummoned(true);
      await summonAdmin({ tradeId }).unwrap();
      toast.success('Admin has been notified and will review your case');
      refetchCase();
    } catch (error: any) {
      setHasJustSummoned(false);
      console.error('Failed to summon admin:', error);
      toast.error(error.data?.message || 'Failed to summon admin. Please try again.');
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="contact-modal-overlay" onClick={onClose}>
      <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
        <div className="contact-modal-header">
          <h2 className="contact-modal-title">Contact {receiverUsername}</h2>
          <button className="contact-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="contact-modal-content">
          <div className="contact-user-info">
            {receiverAvatar ? (
              <img src={receiverAvatar} alt={receiverUsername} className="contact-avatar" />
            ) : (
              <div className="contact-avatar-placeholder">
                {receiverUsername.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="contact-username">{receiverUsername}</span>
          </div>

          <div className="contact-form-group">
            <label className="contact-label">Message</label>
            <textarea
              ref={textareaRef}
              className="contact-textarea"
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              maxLength={2000}
            />
            <CharacterCounter
              currentLength={message.length}
              maxLength={2000}
              warningThreshold={1800}
            />
          </div>

          <div className="contact-actions">
            <div className="emoji-picker-container">
              <button
                ref={emojiButtonRef}
                className="emoji-picker-button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                type="button"
              >
                😀
              </button>
              {showEmojiPicker && (
                <EmojiPicker
                  onEmojiSelect={handleEmojiSelect}
                  onClose={() => setShowEmojiPicker(false)}
                  buttonRef={emojiButtonRef}
                />
              )}
            </div>

            {tradeId && !(caseData?.exists || hasJustSummoned) && (
              <MagicButton
                label="Summon Admin"
                color="black"
                onPress={handleSummonAdmin}
                isDisabled={isSummoning}
              />
            )}

            <MagicButton
              label={
                isLoading
                  ? 'Sending...'
                  : rateLimitRetryAfter > 0
                  ? `Wait ${rateLimitRetryAfter}s`
                  : 'Send Message'
              }
              color="purple"
              onPress={handleSend}
              isDisabled={
                !message.trim() ||
                message.length > 2000 ||
                isLoading ||
                rateLimitRetryAfter > 0
              }
            />
          </div>

          {tradeId && (caseData?.exists || hasJustSummoned) && (
            <div className="contact-summon-admin-section">
              <div className="case-summoned-status">
                <img
                  src={AssetUrls.icons.navigation.adminCases}
                  alt="Admin Summoned"
                  style={{ width: 20, height: 20 }}
                />
                <div>
                  <p className="summoned-text">Admin Summoned</p>
                  <p className="summoned-details">
                    {caseData?.summonedBy && `Case created by ${caseData.summonedBy}`}
                    {caseData?.alsoRequestedBy && ` and ${caseData.alsoRequestedBy}`}
                    {hasJustSummoned && !caseData?.exists && 'An admin will review shortly'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
